import { OrderAction, AssetType, DebitOrCredit, } from '@tradingblock/types';
import _ from 'lodash';
import { isCalendar } from '..';
const RatioForPrice = {
    [AssetType.Equity.toFixed()]: 0.01,
    [AssetType.Option.toFixed()]: 1,
};
const getLegMarketValueTotalForPrice = (leg, price) => {
    const at = leg.AssetType;
    const priceRatio = RatioForPrice[at.toFixed()];
    const quantity = leg.SpreadRatio || 1;
    return priceRatio * quantity * price;
};
const getAggregatedBidAsk = (legs) => {
    return _.reduce(legs, (acc, l) => {
        return Object.assign({}, acc, { bid: acc.bid + l.bid, ask: acc.ask + l.ask });
    }, { bid: 0, ask: 0 });
};
const minOrZero = (vals) => _.min(vals) || 0;
const maxOrZero = (vals) => _.max(vals) || 0;
const getLegBidAsk = (legs, quotes) => {
    return _.reduce(legs, (acc, leg, legSymbol) => {
        const legQuote = _.find(quotes, q => q.Symbol === legSymbol);
        if (legQuote && leg && legSymbol) {
            // const isBuy = leg.Action === OrderAction.Buy;
            // const marketBidPrice = isBuy ? legQuote.BidPrice : legQuote.AskPrice;
            // const marketAskPrice = isBuy ? legQuote.AskPrice : legQuote.BidPrice;
            const legbid = getLegMarketValueTotalForPrice(leg, legQuote.BidPrice);
            const legask = getLegMarketValueTotalForPrice(leg, legQuote.AskPrice);
            return [
                ...acc,
                {
                    leg,
                    bid: legbid,
                    ask: legask,
                    action: leg.Action,
                },
            ];
        }
        else {
            return acc;
        }
    }, []);
};
export const calculateOrderPrice = (legs, quotes, action, gcd, strategy) => {
    if (strategy === 'Put Calendar' || strategy === 'Call Calendar') {
        let option_legs = Object.values(legs).filter(l => l.AssetType === AssetType.Option);
        let option_legs_keys = Object.keys(legs).filter(l => legs[l].AssetType === AssetType.Option);
        if (option_legs !== undefined &&
            option_legs.length === 2 &&
            option_legs[0] !== undefined &&
            option_legs[1] !== undefined &&
            option_legs[0].Expiration !== undefined &&
            option_legs[1].Expiration !== undefined) {
            const later_exp_option = option_legs[0].Expiration.date > option_legs[1].Expiration.date ? 0 : 1;
            const earlier_exp_option = option_legs[0].Expiration.date < option_legs[1].Expiration.date ? 0 : 1;
            const later_exp_option_quote = quotes.find(q => q.Symbol === option_legs_keys[later_exp_option]);
            const earlier_exp_option_quote = quotes.find(q => q.Symbol === option_legs_keys[earlier_exp_option]);
            const totalMultiple = 100;
            if (later_exp_option_quote !== undefined && earlier_exp_option_quote !== undefined) {
                const bid = later_exp_option_quote.BidPrice - earlier_exp_option_quote.AskPrice;
                const ask = later_exp_option_quote.AskPrice - earlier_exp_option_quote.BidPrice;
                return {
                    bid: _.round(bid, 2),
                    ask: _.round(ask, 2),
                    mid: _.round((ask - bid) / 2 + bid, 3),
                    totalMultiple,
                    gcd,
                };
            }
        }
    }
    let option_legs = Object.values(legs).filter(l => l.AssetType === AssetType.Option);
    const maxRatio = _.max(_.map(legs, l => RatioForPrice[l.AssetType.toFixed()])) || 1;
    const legPrices = getLegBidAsk(legs, quotes);
    const shortLegs = _.filter(legPrices, l => l.action === OrderAction.Sell);
    const longLegs = _.filter(legPrices, l => l.action === OrderAction.Buy);
    const shorts = getAggregatedBidAsk(shortLegs);
    const longs = getAggregatedBidAsk(longLegs);
    const bidValue = action === OrderAction.Buy ? longs.bid - shorts.ask : shorts.bid - longs.ask;
    const askValue = action === OrderAction.Buy ? longs.ask - shorts.bid : shorts.ask - longs.bid;
    const bid = minOrZero([bidValue, askValue]) / gcd / maxRatio;
    const ask = maxOrZero([bidValue, askValue]) / gcd / maxRatio;
    const mid = (ask - bid) / 2 + bid;
    const totalMultiple = option_legs.length > 0 ? 100 : 1;
    return {
        ask: _.round(ask, 2),
        bid: _.round(bid, 2),
        mid: _.round(mid, 2),
        totalMultiple,
        gcd,
    };
};
export const getDebitOrCredit = (data) => {
    const { action, strategy, currentDebitOrCredit, price, updateCreditOrDebit, legs, mid, match } = data;
    const orderActionValue = isCalendar(strategy) ? OrderAction.Buy : action;
    const defaultDebitOrCredit = orderActionValue === OrderAction.Buy ? DebitOrCredit.Debit : DebitOrCredit.Credit;
    let debitCredit = currentDebitOrCredit;
    const isCalendarMatch = match && (match.info.Name === 'Call Calendar' || match.info.Name === 'Put Calendar');
    const isCallVerticalMatch = match && match.info.Name === 'Call Vertical';
    const isPutVerticalMatch = match && match.info.Name === 'Put Vertical';
    const isCoveredCallMatch = match && match.info.Name === 'Covered Call';
    const isCallButterflyMatch = match && match.info.Name === 'Call Butterfly';
    const isPutButterflyMatch = match && match.info.Name === 'Put Butterfly';
    const isIronButterflyMatch = match && match.info.Name === 'Iron Butterfly';
    const isIronCondorMatch = match && match.info.Name === 'Iron Condor';
    const isCallCondorMatch = match && match.info.Name === 'Call Condor';
    const isPutCondorMatch = match && match.info.Name === 'Put Condor';
    const isCall = match && match.info.Name === 'Call';
    const isPut = match && match.info.Name === 'Put';
    if (strategy === 'Custom' && _.every(legs, l => l.Action === OrderAction.Buy)) {
        debitCredit = DebitOrCredit.Debit;
    }
    else if (strategy === 'Custom' && _.every(legs, l => l.Action === OrderAction.Sell)) {
        debitCredit = DebitOrCredit.Credit;
    }
    else if (updateCreditOrDebit && ((mid !== undefined && mid < 0) || price < 0)) {
        debitCredit = defaultDebitOrCredit === DebitOrCredit.Debit ? DebitOrCredit.Credit : DebitOrCredit.Debit;
    }
    else if (updateCreditOrDebit && ((mid !== undefined && mid >= 0) || price >= 0)) {
        debitCredit = defaultDebitOrCredit;
    }
    if (strategy === 'Call Calendar' || strategy === 'Put Calendar') {
        if (price !== undefined) {
            if (price > 0 && action === OrderAction.Buy) {
                debitCredit = DebitOrCredit.Debit;
            }
            else if (price > 0 && action === OrderAction.Sell) {
                debitCredit = DebitOrCredit.Credit;
            }
            if (price < 0 && action === OrderAction.Buy) {
                debitCredit = DebitOrCredit.Credit;
            }
            else if (price < 0 && action === OrderAction.Sell) {
                debitCredit = DebitOrCredit.Debit;
            }
        }
    }
    // find the latest expiring leg
    const findLatestExpiration = legs => {
        let latestExpiration = null;
        let latestExpirationDate = new Date(0); // Initialize with a very early date
        for (const legKey in legs) {
            const leg = legs[legKey];
            const expirationDate = new Date(leg.Expiration.date);
            if (expirationDate > latestExpirationDate) {
                latestExpirationDate = expirationDate;
                latestExpiration = leg;
            }
        }
        return latestExpiration;
    };
    // find the lowest strike leg
    const findLowestStrike = legs => {
        let lowestStrike = null;
        let lowestStrikeValue = 0;
        for (const legKey in legs) {
            const leg = legs[legKey];
            const strike = leg.Strike;
            if (lowestStrike === null || strike < lowestStrikeValue) {
                lowestStrikeValue = strike;
                lowestStrike = leg;
            }
        }
        return lowestStrike;
    };
    const findShareLeg = legs => {
        let shareLeg = null;
        for (const legKey in legs) {
            const leg = legs[legKey];
            if (leg.AssetType === AssetType.Equity) {
                shareLeg = leg;
                break;
            }
        }
        return shareLeg;
    };
    if (isCall) {
        const lowestStrikeLeg = findLowestStrike(legs);
        if (lowestStrikeLeg) {
            if (updateCreditOrDebit) {
                debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Debit : DebitOrCredit.Credit;
            }
        }
    }
    if (isPut) {
        const lowestStrikeLeg = findLowestStrike(legs);
        if (lowestStrikeLeg) {
            if (updateCreditOrDebit) {
                debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Debit : DebitOrCredit.Credit;
            }
        }
    }
    if (isCallVerticalMatch) {
        // use the lowest strike leg action to determine the debit or credit
        const lowestStrikeLeg = findLowestStrike(legs);
        if (lowestStrikeLeg) {
            if (updateCreditOrDebit) {
                // for a call vertical the order is considered a buy if you are buying the lower strike and selling the higher strike
                debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Debit : DebitOrCredit.Credit;
            }
        }
    }
    if (isPutVerticalMatch) {
        // use the lowest strike leg action to determine the debit or credit
        const lowestStrikeLeg = findLowestStrike(legs);
        if (lowestStrikeLeg) {
            if (updateCreditOrDebit) {
                // for a put vertical the order is considered a buy if you are buying the higher strike and selling the lower strike
                debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Credit : DebitOrCredit.Debit;
            }
        }
    }
    if (isCoveredCallMatch) {
        // use the share leg action to determine the debit or credit
        const shareLeg = findShareLeg(legs);
        if (shareLeg) {
            if (updateCreditOrDebit) {
                // for a covered call the order is considered a buy if you are buying the share and selling the call
                debitCredit = shareLeg.Action === OrderAction.Buy ? DebitOrCredit.Debit : DebitOrCredit.Credit;
            }
        }
    }
    if (isCallButterflyMatch) {
        // use the lowest strike leg action to determine the debit or credit
        const lowestStrikeLeg = findLowestStrike(legs);
        if (lowestStrikeLeg) {
            if (updateCreditOrDebit) {
                // for a call butterfly the order is considered a buy if you are buying the lower strike and higher strike and selling the middle strike
                debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Debit : DebitOrCredit.Credit;
            }
        }
    }
    if (isPutButterflyMatch) {
        // use the lowest strike leg action to determine the debit or credit
        const lowestStrikeLeg = findLowestStrike(legs);
        if (lowestStrikeLeg) {
            if (updateCreditOrDebit) {
                // for a put butterfly the order is considered a buy if you are buying the middle strike and selling the lower strike and higher strike
                debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Debit : DebitOrCredit.Credit;
            }
        }
    }
    if (isIronButterflyMatch) {
        // use the lowest strike leg action to determine the debit or credit
        const lowestStrikeLeg = findLowestStrike(legs);
        if (lowestStrikeLeg) {
            if (updateCreditOrDebit) {
                // for an iron butterfly the order is considered a buy if you are buying the lower strike and higher strike and selling the middle strike
                debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Debit : DebitOrCredit.Credit;
            }
        }
    }
    if (isIronCondorMatch) {
        // use the lowest strike leg action to determine the debit or credit
        const lowestStrikeLeg = findLowestStrike(legs);
        if (lowestStrikeLeg) {
            if (updateCreditOrDebit) {
                // for an iron condor the order is considered a buy if you are buying the lower strike and higher strike and selling the middle strike
                debitCredit = lowestStrikeLeg.Action === OrderAction.Sell ? DebitOrCredit.Debit : DebitOrCredit.Credit;
            }
        }
    }
    if (isCallCondorMatch) {
        // use the lowest strike leg action to determine the debit or credit
        const lowestStrikeLeg = findLowestStrike(legs);
        if (lowestStrikeLeg) {
            if (updateCreditOrDebit) {
                if (price > 0) {
                    debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Debit : DebitOrCredit.Credit;
                }
                else if (price < 0) {
                    debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Credit : DebitOrCredit.Debit;
                }
            }
        }
    }
    if (isPutCondorMatch) {
        // use the lowest strike leg action to determine the debit or credit
        const lowestStrikeLeg = findLowestStrike(legs);
        if (lowestStrikeLeg) {
            if (updateCreditOrDebit) {
                if (price > 0) {
                    debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Debit : DebitOrCredit.Credit;
                }
                else if (price < 0) {
                    debitCredit = lowestStrikeLeg.Action === OrderAction.Buy ? DebitOrCredit.Credit : DebitOrCredit.Debit;
                }
            }
        }
    }
    return debitCredit;
};
export const getBestPrice = ({ ask, // The current asking price in the market
bid, // The current bidding price in the market
last, // If settlement is not set, the last traded price of the asset, else the settlement price
defaultValue, // Default value to use under certain conditions
isSettlementSet, // Boolean indicating if the settlement price is set
useCalculatedPnL, }) => {
    // Normalize ask and bid values to 0 if they are undefined or null, assuming negative prices are invalid
    const askValue = _.isNil(ask) ? 0 : ask;
    const bidValue = _.isNil(bid) ? 0 : bid;
    // If the settlement is set, return the last price immediately
    if (isSettlementSet === true || useCalculatedPnL === true) {
        return last;
    }
    // If both bid and ask prices are non-negative
    if (bidValue >= 0 && askValue > 0) {
        // If the last price is less than or equal to the bid, return the bid value
        if (last <= bidValue) {
            return bidValue;
        }
        // Else if the last price is greater than the ask, return the ask value
        else if (last > askValue) {
            return askValue;
        }
        // If the default value is set to 'average', compute the average of ask and bid
        if (defaultValue === 'avgBidAsk') {
            // If the bid value is 0, askValue is greater than 0, and last is greater than askValue,
            // return askValue, otherwise return the last price
            if (bidValue === 0) {
                // If ask is greater than 0, and last is greater than ask, return ask, otherwise return last
                if (askValue > 0 && last > askValue) {
                    return askValue;
                }
                else {
                    return last;
                }
            }
            else {
                // Otherwise, return the average of ask and bid
                return (askValue + bidValue) / 2;
            }
        }
        else {
            // Else if not averaging, return the last price
            return last;
        }
    }
    else {
        // Else if the bid or ask values are not in the expected range
        if (askValue <= 0) {
            // And ask value is non-positive, return the last price
            return last;
        }
        else {
            // Else if the ask value is positive but less than the last, return ask, otherwise return last
            if (askValue < last) {
                return askValue;
            }
            else {
                return last;
            }
        }
    }
};
//# sourceMappingURL=orderPrice.js.map